import { getAppConfig } from '../config/runtimeConfig';

export const ACCOUNT_REGIONS = [
  { value: 'ALL', label: 'All regions' },
  { value: 'EU_CENTRAL_1', label: 'EU Central 1' },
  { value: 'US_EAST_1', label: 'US East 1' },
];

const ACCOUNT_RESULT_FIELDS = `
  region
  errorInfo { error errorCode message }
  userData {
    userId
    email
    firstName
    lastName
    nickName
    language
  }
  siteData {
    siteId
    siteName
    siteAddress
    city
    state
    country
    zipCode
    createdAt
    updatedAt
    sections {
      sectionId
      sectionName
      areas {
        areaId
        areaName
        defaultArea
        hiddenArea
        devices {
          deviceId
          childDeviceId
          deviceName
          deviceStatus
          deviceIcon
          createdAt
          updatedAt
          userId
        }
      }
    }
  }
`;

const DEVICE_DETAIL_FIELDS = `
  region
  errorInfo { error errorCode message }
  deviceData {
    deviceId
    childDeviceId
    deviceName
    deviceIcon
    deviceRole
    deviceSubType
    deviceType
    connectionStatus
    firmwareVersion
    hardwareVersion
    createdAt
    updatedAt
    connectionData {
      interfaceType
      protocol
      version
    }
    manufacturer {
      name
      model
      serialNumber
    }
    interface {
      model
      serialNumber
      wifiMacAddress
      bluetoothMacAddress
      hardwareVersion
      firmwareVersion
    }
    location {
      country
      latitude
      longitude
      timezone { id utcOffset }
    }
  }
`;

async function graphqlRequest(query, variables, idToken) {
  const appsyncUrl = getAppConfig('REACT_APP_FIRMWARE_APPSYNC_URL').replace(/\/+$/, '');
  if (!idToken) throw new Error('Missing ID token. Please sign in again.');
  if (!appsyncUrl) throw new Error('REACT_APP_FIRMWARE_APPSYNC_URL is not configured.');

  const response = await fetch(appsyncUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: idToken },
    body: JSON.stringify({ query, variables }),
  });
  const result = await response.json().catch(() => ({}));

  if (!response.ok) throw new Error(result?.message || `Account search request failed (${response.status}).`);
  if (result.errors?.length) throw new Error(result.errors[0].message || 'Account search request failed.');
  return result.data;
}

function requireResults(results, operation) {
  const values = Array.isArray(results) ? results : [];
  const error = values.find((item) => item?.errorInfo)?.errorInfo;
  const successful = values.filter((item) => !item?.errorInfo);

  if (successful.length) return successful;
  if (error) throw new Error(error.message || error.error || `${operation} failed.`);
  return [];
}

export function regionInputFor(region) {
  const normalized = String(region || '').toUpperCase().replace(/-/g, '_');
  if (normalized === 'EU_CENTRAL_1' || normalized === 'US_EAST_1') return normalized;
  return 'ALL';
}

export async function getAccountInfoByEmail(idToken, { email, regionId = 'ALL' }) {
  const query = `query GetAccountInfoByEmail($email: AWSEmail!, $regionId: RegionName!) {
    getAccountInfoByEmail(email: $email, regionId: $regionId) { ${ACCOUNT_RESULT_FIELDS} }
  }`;
  const data = await graphqlRequest(query, { email, regionId }, idToken);
  return requireResults(data?.getAccountInfoByEmail, 'Account lookup');
}

export async function getAccountInfoByDeviceId(idToken, { deviceId, regionId = 'ALL' }) {
  const query = `query GetAccountInfoByDeviceId($deviceId: ID!, $regionId: RegionName!) {
    getAccountInfoByDeviceId(deviceId: $deviceId, regionId: $regionId) { ${ACCOUNT_RESULT_FIELDS} }
  }`;
  const data = await graphqlRequest(query, { deviceId, regionId }, idToken);
  return requireResults(data?.getAccountInfoByDeviceId, 'Device lookup');
}

export async function getDeviceDataById(idToken, { deviceId, childDeviceId, regionId }) {
  const query = `query GetDeviceDataById($deviceId: ID!, $childDeviceId: String!, $regionId: RegionName!) {
    deviceDataById(deviceId: $deviceId, childDeviceId: $childDeviceId, regionId: $regionId) { ${DEVICE_DETAIL_FIELDS} }
  }`;
  const data = await graphqlRequest(query, {
    deviceId,
    childDeviceId: childDeviceId || '',
    regionId: regionInputFor(regionId),
  }, idToken);
  const results = requireResults(data?.deviceDataById, 'Device detail lookup');
  return results.find((item) => item?.deviceData) || null;
}
